import { User } from "@/types/user";
import { Paper } from "@/types/paper";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Building2, LinkIcon, Twitter, Linkedin, FileText, CalendarRange, BookOpen } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

interface AuthorProfileCardProps {
  author: User;
  paperCount: number;
  papers?: Paper[];
}

const Stat = ({ value, label, icon: Icon }: { value: string | number; label: string; icon: React.ComponentType<{ className?: string }> }) => (
  <div className="flex flex-col items-start gap-1 px-5 py-4 rounded-xl bg-background/60 backdrop-blur-sm border border-border/60 min-w-[120px]">
    <div className="flex items-center gap-1.5 text-muted-foreground">
      <Icon className="h-3.5 w-3.5" />
      <span className="text-[10px] uppercase tracking-[0.14em] font-medium">{label}</span>
    </div>
    <span className="text-3xl font-semibold tracking-tight text-foreground tabular-nums">{value}</span>
  </div>
);

const AuthorProfileCard = ({ author, paperCount, papers = [] }: AuthorProfileCardProps) => {
  const { user } = useAuth();
  const isAuthenticated = !!user;

  // Year range derived from already-fetched papers (no extra queries)
  const years = papers
    .map((p) => (p.publishedAt ? new Date(p.publishedAt).getFullYear() : NaN))
    .filter((y) => Number.isFinite(y)) as number[];
  const firstYear = years.length ? Math.min(...years) : null;
  const latestYear = years.length ? Math.max(...years) : null;
  const yearsActive = firstYear && latestYear ? Math.max(1, latestYear - firstYear + 1) : null;
  const yearsLabel = firstYear && latestYear
    ? (firstYear === latestYear ? `${firstYear}` : `${firstYear} – ${latestYear}`)
    : "—";

  const initials = author.name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <section
      aria-label="Researcher profile"
      className="relative overflow-hidden rounded-3xl border border-border/60 bg-card shadow-sm"
    >
      {/* Top accent rail — brand continuity with paper pages */}
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-px"
        style={{ background: "var(--gradient-primary, linear-gradient(90deg, transparent, hsl(var(--primary)), transparent))" }}
      />
      {/* Layered backdrop */}
      <div
        aria-hidden
        className="absolute inset-0 -z-0 opacity-[0.55] pointer-events-none"
        style={{
          background:
            "radial-gradient(1200px 240px at 10% -10%, hsl(var(--primary) / 0.10), transparent 60%), radial-gradient(800px 200px at 110% 0%, hsl(var(--accent) / 0.08), transparent 60%)",
        }}
      />

      <div className="relative p-6 md:p-10">
        <div className="flex flex-col md:flex-row md:items-start md:gap-10 gap-6">
          {/* Avatar */}
          <div className="relative shrink-0">
            <div
              aria-hidden
              className="absolute -inset-1.5 rounded-full blur-md opacity-50"
              style={{ background: "var(--gradient-primary, linear-gradient(135deg, hsl(var(--primary)), hsl(var(--primary) / 0.4)))" }}
            />
            <Avatar className="relative h-28 w-28 md:h-32 md:w-32 ring-2 ring-background shadow-lg">
              {author.avatar ? (
                <AvatarImage src={author.avatar} alt={author.name} />
              ) : (
                <AvatarFallback className="bg-primary text-primary-foreground text-3xl font-semibold">
                  {initials || author.name.charAt(0)}
                </AvatarFallback>
              )}
            </Avatar>
          </div>

          {/* Identity */}
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              {author.role && (
                <Badge variant="secondary" className="text-[10px] uppercase tracking-[0.14em] font-medium">
                  {author.role}
                </Badge>
              )}
              {author.orcid && (
                <Badge variant="outline" className="text-[10px] uppercase tracking-[0.14em] font-medium">
                  ORCID verified
                </Badge>
              )}
            </div>

            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-foreground leading-tight">
              {author.name}
            </h1>

            {author.institution && (
              <p className="mt-2 flex items-center gap-2 text-muted-foreground">
                <Building2 className="h-4 w-4" />
                <span className="truncate">{author.institution}</span>
              </p>
            )}

            {author.bio && (
              <p className="mt-4 text-foreground/85 leading-relaxed max-w-2xl">{author.bio}</p>
            )}

            {/* Social rail — gated to authenticated users (anti-scraping) */}
            {isAuthenticated && (author.website || author.twitter || author.linkedin || author.orcid) && (
              <div className="mt-5 flex flex-wrap gap-2">
                {author.website && (
                  <a href={author.website} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="h-8 gap-1.5">
                      <LinkIcon className="h-3.5 w-3.5" /> Website
                    </Button>
                  </a>
                )}
                {author.twitter && (
                  <a href={author.twitter} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="h-8 gap-1.5">
                      <Twitter className="h-3.5 w-3.5" /> Twitter
                    </Button>
                  </a>
                )}
                {author.linkedin && (
                  <a href={author.linkedin} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="h-8 gap-1.5">
                      <Linkedin className="h-3.5 w-3.5" /> LinkedIn
                    </Button>
                  </a>
                )}
                {author.orcid && (
                  <a href={author.orcid} target="_blank" rel="noopener noreferrer">
                    <Button variant="outline" size="sm" className="h-8 gap-1.5">
                      <FileText className="h-3.5 w-3.5" /> ORCID
                    </Button>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Stats strip */}
        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Stat value={paperCount} label="Publications" icon={BookOpen} />
          <Stat value={yearsActive ?? "—"} label="Years active" icon={CalendarRange} />
          <Stat value={yearsLabel} label="Publication span" icon={CalendarRange} />
        </div>
      </div>
    </section>
  );
};

export default AuthorProfileCard;
